import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { Search, SlidersHorizontal, ChevronLeft, ChevronRight, Calculator, ShoppingCart, Check, X, ArrowRight, Info } from 'lucide-react';

const FabricCard = ({ fabric, onSelect, selected }) => {
  const imgUrl = fabric.thumbnail?.startsWith('http') ? fabric.thumbnail : fabric.image;
  return (
    <div
      onClick={() => onSelect(fabric)}
      className={`cursor-pointer bg-white rounded-xl overflow-hidden border-2 transition-all duration-200 hover:shadow-md group ${
        selected ? 'border-orange-500 shadow-orange-100 shadow-md' : 'border-gray-100 hover:border-orange-200'
      }`}
    >
      <div className="relative h-44 overflow-hidden bg-gray-50">
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={fabric.name || `${fabric.code1} ${fabric.code2}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={e => { e.target.src = 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&q=60'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-16 h-16 rounded-full" style={{ backgroundColor: getColorFromName(fabric.subcat3 || '') }} />
          </div>
        )}
        {selected && (
          <div className="absolute top-2 right-2 w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center">
            <Check size={14} className="text-white" />
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2">
          <span className="text-white text-xs font-bold">{fabric.supplier}</span>
        </div>
      </div>
      <div className="p-3">
        <p className="font-bold text-gray-900 text-sm truncate">{fabric.code1 || ''} {fabric.code2 || ''}</p>
        <p className="text-orange-500 text-xs font-medium truncate">{fabric.gamme}</p>
        <p className="text-gray-400 text-xs mt-0.5">{fabric.subcat3 || fabric.subcat1 || ''}</p>
      </div>
    </div>
  );
};

const getColorFromName = (name) => {
  const colors = { 'BEIGE': '#F5F0E8', 'BLANC': '#F8F8F8', 'GRIS': '#9CA3AF', 'MARRON': '#92400E', 'VERT': '#16A34A', 'ROUGE': '#DC2626', 'BLEU': '#2563EB', 'NOIR': '#111827', 'ORANGE': '#F97316', 'JAUNE': '#EAB308' };
  for (const [key, val] of Object.entries(colors)) {
    if (name.toUpperCase().includes(key)) return val;
  }
  return '#D1D5DB';
};

const TuilesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fabrics, setFabrics] = useState([]);
  const [collections, setCollections] = useState([]);
  const [filters, setFilters] = useState({ suppliers: [], gammes: [], colors: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedGamme, setSelectedGamme] = useState('');
  const [source, setSource] = useState('tsb');
  const [page, setPage] = useState(0);
  const [selectedFabric, setSelectedFabric] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [calcForm, setCalcForm] = useState({ largeur: 300, avancee: 200, hauteur_lambrequin: 30 });
  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const LIMIT = 20;

  const fetchFabrics = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (search.trim()) {
        res = await axios.get(`${API}/fabrics/search`, {
          params: { q: search, source, limit: LIMIT, supplier: selectedSupplier || undefined, gamme: selectedGamme || undefined }
        });
        setFabrics(res.data?.results || res.data || []);
      } else {
        res = await axios.get(`${API}/fabrics/products`, {
          params: { source, limit: LIMIT, skip: page * LIMIT, supplier: selectedSupplier || undefined }
        });
        setFabrics(res.data || []);
      }
    } catch (e) { console.error(e); setFabrics([]); }
    finally { setLoading(false); }
  }, [search, source, selectedSupplier, selectedGamme, page]);

  const fetchMeta = useCallback(async () => {
    try {
      const [colRes, filRes] = await Promise.all([
        axios.get(`${API}/fabrics/collections`, { params: { source } }),
        axios.get(`${API}/fabrics/filters`, { params: { source } })
      ]);
      setCollections(colRes.data || []);
      setFilters(filRes.data || {});
    } catch (e) { console.error(e); }
  }, [source]);

  useEffect(() => { fetchFabrics(); }, [fetchFabrics]);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  const handleCalculate = async () => {
    if (!selectedFabric) return;
    setCalcLoading(true);
    setCalcResult(null);
    try {
      const res = await axios.post(`${API}/fabrics/calculate`, {
        product_id: selectedFabric.id,
        largeur: calcForm.largeur,
        avancee: calcForm.avancee,
        hauteur_lambrequin: calcForm.hauteur_lambrequin,
        source
      });
      setCalcResult(res.data);
    } catch (e) { console.error(e); }
    finally { setCalcLoading(false); }
  };

  const handleOrder = async () => {
    if (!calcResult || !selectedFabric) return;
    try {
      await axios.post(`${API}/pro/orders`, {
        items: [{
          type: 'toile_mesure',
          product_id: selectedFabric.id,
          product_name: `${selectedFabric.supplier} - ${selectedFabric.code1 || ''} ${selectedFabric.code2 || ''}`,
          details: {
            largeur: calcForm.largeur,
            avancee: calcForm.avancee,
            hauteur_lambrequin: calcForm.hauteur_lambrequin,
            gamme: selectedFabric.gamme,
            source,
            ...calcResult
          },
          quantity: 1,
          unit_price: calcResult.price_total,
          total_price: calcResult.price_total
        }]
      }, { withCredentials: true });
      setOrderSuccess(true);
      setTimeout(() => { setOrderSuccess(false); setShowCalculator(false); setCalcResult(null); }, 3000);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1a1a1a] py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
            <Link to="/mon-espace" className="hover:text-orange-400">Espace Pro</Link>
            <span>/</span>
            <span className="text-white">Toiles sur mesure</span>
          </div>
          <h1 className="text-3xl font-black text-white mb-2">Catalogue Toiles sur Mesure</h1>
          <p className="text-gray-400">Dickson, Sauleda, Sattler, Recasens et plus. Calculez votre prix instantanément.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Source Selector */}
        <div className="flex items-center gap-3 mb-6">
          <span className="text-sm text-gray-500 font-medium">Source :</span>
          {[
            { value: 'tsb', label: 'ToilsStoreBanne.com' },
            { value: 'qualitoiles', label: 'Qualitoiles.com' },
          ].map(s => (
            <button
              key={s.value}
              onClick={() => { setSource(s.value); setPage(0); }}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                source === s.value ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-orange-300'
              }`}
            >{s.label}</button>
          ))}
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher une toile (ex: Dickson, rouge, 8812...)"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-orange-400 transition-all"
              />
            </div>
            <select
              value={selectedSupplier}
              onChange={e => { setSelectedSupplier(e.target.value); setPage(0); }}
              className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-orange-400"
            >
              <option value="">Tous les fournisseurs</option>
              {filters.suppliers?.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array(10).fill(0).map((_, i) => (
              <div key={i} className="bg-gray-100 h-64 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <p className="text-gray-400 text-sm mb-4">{fabrics.length} toile{fabrics.length > 1 ? 's' : ''} trouvée{fabrics.length > 1 ? 's' : ''}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {fabrics.map(f => (
                <FabricCard
                  key={f.id}
                  fabric={f}
                  onSelect={(fab) => { setSelectedFabric(fab); setShowCalculator(true); setCalcResult(null); }}
                  selected={selectedFabric?.id === f.id}
                />
              ))}
            </div>

            {/* Pagination */}
            {!search && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <button
                  onClick={() => setPage(p => Math.max(0, p-1))}
                  disabled={page === 0}
                  className="p-2 rounded-lg border border-gray-200 disabled:opacity-30 hover:border-orange-400 transition-all"
                >
                  <ChevronLeft size={18} />
                </button>
                <span className="text-sm text-gray-600">Page {page+1}</span>
                <button
                  onClick={() => setPage(p => p+1)}
                  disabled={fabrics.length < LIMIT}
                  className="p-2 rounded-lg border border-gray-200 disabled:opacity-30 hover:border-orange-400 transition-all"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Calculator Panel */}
      {showCalculator && selectedFabric && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="font-black text-gray-900">Calculer mon prix</h3>
                <p className="text-orange-500 text-sm font-medium">{selectedFabric.supplier} – {selectedFabric.code1} {selectedFabric.code2}</p>
              </div>
              <button onClick={() => { setShowCalculator(false); setCalcResult(null); }} className="text-gray-400 hover:text-gray-600">
                <X size={22} />
              </button>
            </div>

            {/* Fabric thumbnail */}
            {selectedFabric.thumbnail?.startsWith('http') && (
              <div className="px-5 pt-4">
                <img src={selectedFabric.thumbnail} alt="" className="h-24 w-full object-cover rounded-lg" />
              </div>
            )}

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { name: 'largeur', label: 'Largeur (cm)', min: 50, max: 600, placeholder: '300' },
                  { name: 'avancee', label: 'Avancée (cm)', min: 50, max: 400, placeholder: '200' },
                  { name: 'hauteur_lambrequin', label: 'Lambrequin (cm)', min: 10, max: 80, placeholder: '30' },
                ].map(field => (
                  <div key={field.name}>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">{field.label}</label>
                    <input
                      type="number"
                      value={calcForm[field.name]}
                      min={field.min}
                      max={field.max}
                      onChange={e => setCalcForm(prev => ({ ...prev, [field.name]: parseFloat(e.target.value) || 0 }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                      placeholder={field.placeholder}
                    />
                  </div>
                ))}
              </div>

              <button
                onClick={handleCalculate}
                disabled={calcLoading}
                className="w-full flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-bold py-3 rounded-xl transition-all"
              >
                <Calculator size={18} />
                {calcLoading ? 'Calcul...' : 'Calculer le prix'}
              </button>

              {calcResult && (
                <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div><span className="text-gray-500">Largeur totale :</span> <strong>{calcResult.largeur_totale} cm</strong></div>
                    <div><span className="text-gray-500">Nb laizes :</span> <strong>{calcResult.nombre_laizes}</strong></div>
                    <div><span className="text-gray-500">Métrage :</span> <strong>{calcResult.metrage_total} m</strong></div>
                    <div><span className="text-gray-500">Poids :</span> <strong>{calcResult.poids_total} g</strong></div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-orange-200">
                    <span className="font-bold text-gray-900">Prix total HT</span>
                    <span className="text-2xl font-black text-orange-600">{calcResult.price_total?.toFixed(2)} €</span>
                  </div>

                  {orderSuccess ? (
                    <div className="mt-3 flex items-center gap-2 text-green-600 font-semibold text-sm">
                      <Check size={18} /> Commande enregistrée !
                    </div>
                  ) : (
                    <button
                      onClick={handleOrder}
                      disabled={user?.status !== 'approved'}
                      className="mt-3 w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white font-bold py-3 rounded-xl transition-all"
                    >
                      <ShoppingCart size={16} />
                      {user?.status !== 'approved' ? 'Compte en attente de validation' : 'Passer commande'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TuilesPage;
